// Natural Draft Burners.
//
// Everything here used to be a module-level constant in shared code (validation.ts,
// hiddenQuestions.ts, numericSuffix.ts, runtime.ts, fahSections.ts, an ad-hoc Set in
// SaveSubmitButton). The values are UNCHANGED -- this is where they live now, so
// that adding a configurator cannot alter them and they cannot reach a configurator
// that does not want them.
import type { ProductProfile } from "./profile";
import { ndbBurnerModel } from "./ndbBurnerModel";

// WHEN A PIPE UNION IS COMPULSORY. Kevin, 2026-09-21: "if any of these are true (unit is a box
// type) or (hinge/otype and cell is 26 or bigger) or (mixer size is 3" or bigger) or (if it has
// a compound injector or single stage) then pipe with union is always yes".
//
// Built from lists rather than written out, because the same four conditions have to hold in
// two places -- this rule, and the operation that forces the value -- and a hand-copied
// expression is how the two drift.
//
// CELL SIZE IS READ FROM THE ANSWER CODE. CELL_OD is a dropdown whose codes ARE the inches
// ("26", "28", ...); there is no CELL_OD/CELL_OD value form, despite operation 380 reading one
// (it gets undefined, so that operation's cell-OD branch never fires -- noted, not fixed here).
const CELL_OD_26_PLUS = ["26", "28", "30", "32", "34", "36", "38", "40", "42", "44", "46",
                         "48", "50", "52", "54", "56", "58"];
// "mixer size is 3 inch or bigger" = Main Assembly Size, Kevin's own reading when asked.
const MIXER_3_PLUS = ["BURNER_ASSEMBLY_3", "BURNER_ASSEMBLY_4", "BURNER_ASSEMBLY_5",
                      "BURNER_ASSEMBLY_6"];
// "a compound injector or single stage" -- SS is Single Stage, so it is everything on Main
// Assembly Type except Mixer Only. NAMED rather than written as "not Mixer Only", which would
// also be true of an UNANSWERED assembly type and force a union on a configuration that has
// not said yet.
const UNION_ASSEMBLY_TYPES = ["COMPOUND_INJECTOR", "HIGH_PRESSURE_SS", "LOW_PRESSURE_SS"];

const UNION_FORCED_YES = [
  '(__["HOUSING/HOUSING"]=="Box-Type")',
  '(((__["HOUSING/HOUSING"]=="Hinge Type")||(__["HOUSING/HOUSING"]=="O-Type"))&&(' +
    CELL_OD_26_PLUS.map((c) => `(__["CELL_OD/${c}"])`).join("||") + "))",
  MIXER_3_PLUS.map((c) => `(__["BURNER_ASSEMBLY_SIZE/${c}"])`).join("||"),
  UNION_ASSEMBLY_TYPES.map((c) => `(__["MAIN_ASSEMBLY_TYPE/${c}"])`).join("||"),
].join("||");

/** The actuator designs, as the Design table spells them. Shared by the hide rule below and
 *  named in the operation that forces the values, so the two cannot drift apart. */
const ACTUATOR_DESIGNS = ["AC", "ACRN", "ACLN", "EASAC", "EASACRN", "EASACLN"] as const;

export const NDB_PROFILE: ProductProfile = {
  slug: "ndb",

  hiddenQuestionCodes: new Set([
    // hidden 2026-07-20 per request -- not needed right now
    "INLET_CONNECTION_NOTES",
    // hidden 2026-08-06 per request, alongside removing FLAME CELL CORE MATERIAL
    // from the BOM traveler's Configuration section. NATURAL DRAFT ONLY: Flame
    // Cells has a question with this same code and a different answer set, and it
    // is a real field there.
    //
    // Not in the mapping and not in the description: cpq2 writes
    // custitemflamecell_coretype straight onto the item, and the NetSuite decoder
    // never touches it.
    //
    // It IS an ID Key Generator input -- { q: "CORE_TYPE", skip: ["", "na"] } -- so
    // it contributes to the config hash behind the part number. With the question
    // hidden and no default answer, a NEW config leaves it blank, the generator
    // skips it, and two configs differing only by core type hash to the SAME part
    // number. Existing items are unaffected: Edit Config and Item Template restore
    // the stored answer, so their hash is stable.
    "CORE_TYPE",
  ]),

  // Empty groups in the export, plus two the Customer Requirements tab should not
  // show. Kept in step with TabPanel rendering AND validation, which read the same
  // profile, so a group cannot be hidden in one and required in the other.
  hiddenGroupCodesByTab: {
    INPUTS_TAB: new Set([
      "ADDITIONAL_INTERESTS_GROUP",
      "APPLICATION_CONSIDERATIONS",
      "REQUIREMENTS",
    ]),
    CENTER_TAB: new Set(["UNIT_CAPABILITIES_GROUP", "BURNER_HOUSING"]),
  },

  // Required Heat Duty and Fuel Pressure are here as of 2026-09-01. They are the SIZING
  // inputs -- what the orifice/mixer sizing runs on -- and Kevin ruled that NEITHER launch
  // has to state them: "for required heat duty and for fuel pressure, when on the sales
  // order we dont need to force the user to fill those out", then "quote should not demand
  // them either". They still SHOW, and are still what the sizing reads when given; they
  // just never block a submit.
  //
  // Leaving Required Heat Duty blank does have a visible consequence, and it is the right
  // one: Main Assembly Size is gated on heat-duty thresholds, so its list stays empty until
  // a duty is entered. Anyone who needs a mixer fills it in because the form cannot proceed
  // without it, not because validation refuses to submit.
  //
  // INLET_CONNECTION_NOTES is free-form notes. The flange offset pair OFFSET_2_* is NOT
  // listed: its None/0 options plus their hide rules already make it "answered as None/0"
  // when unused, so the generic check handles it.
  // No option is narrowed beyond what this product's export already says.
  // "Pipe W/ Union Yes" is not offered while 3/8" (stainless) tubing is Yes. Kevin,
  // 2026-09-04: "if stainless 3/8 tubing is yes then pipe with union has to be no" -- the
  // tubing IS the pilot connection, so a union as well is a second joint for the same
  // connection.
  //
  // HALF OF THE RULE, and it needs its other half. This stops the contradiction being
  // CHOSEN; the PIPE_UNION_TUBING_GUARD operation (seq 18.6) stops one that was already
  // chosen from surviving, because a hidden ANSWER keeps its value and PIPE_WITH_UNION is
  // always mapped -- so hiding alone would leave a stale Yes riding into
  // custitempilot_union and the description. Hiding alone is also the wrong shape for a
  // hard rule: it would read as unanswered rather than as No.
  // THE HD IGNITOR TYPE IS OFFERED AGAIN (Kevin, 2026-10-18). The export hides it outright
  // (the always-hide rule (star, slash, star); it has been hidden since cpq2's first import, so this is cpq1's retirement, not
  // ours), but the legacy Configuration tab still records it: 84 sales orders since 2022, 17
  // in 2025, 1 in 2026 -- and those orders carried the PZ ignitor assembly (545739 and kin),
  // where "180 Big Loop" orders carry the bare rod 531201. A customer who orders it could not
  // be pre-filled with it. Its MAPPING token (02) never went away. IC92 stays hidden.
  answerReveals: [{ question: "IGNITOR_ASSEMBLY_TYPE", answers: ["IGNITOR_TYPE_HD"] }],

  answerHideRules: [
    {
      // 3/8" tubing takes "Yes" off the form -- UNLESS one of the conditions that make a union
      // compulsory applies. Kevin, 2026-09-21, asked which wins: the new rule does. Without
      // this exception the operation would force a value whose option is hidden, and the
      // widget would render the question blank.
      question: "PIPE_WITH_UNION",
      answers: ["PIPE_UNION_YES"],
      hiddenWhen: `(__["38_TUBING/YES"])&&!(${UNION_FORCED_YES})`,
    },
  ],

  // AN ACTUATOR DESIGN HAS NO HOOD SPACERS AND NO FACE PLATE. Kevin, 2026-09-21: "if the
  // design is anything actuator (AC, acrn, acln, easac, easacrn, easacln) then you will never
  // have hood spacers, you will never have a face plate so none."
  //
  // Those six are the whole actuator family in the Design table -- the others are CL, CLRN,
  // EAS, EASLN, EASRN, F, LN, RN, STD and T -- so this names them rather than pattern-matching
  // "contains AC", which would also catch nothing today but would catch the wrong thing the
  // first time a design like "ACME" is added.
  //
  // HIDING IS HALF THE RULE. A hidden question keeps its answer, and both of these reach the
  // item: Hood Spacers as custitemhood_spacersize1, the face plate through FACE_PLATE_TYPE.
  // The other half is the cpq2 operation "Actuator design: no hood spacers, no face plate",
  // which forces both to None -- see public/data/ndb/operations.json.
  questionHideRules: [
    {
      questions: ["HOOD_SPACERS", "FACE_PLATE_SIZE"],
      hiddenWhen: ACTUATOR_DESIGNS.map((d) => `(__["DESIGN/DESIGN"]=="${d}")`).join("||"),
    },
  ],

  // ...AND ALWAYS A 1-5/8" HOOD SIGHT TUBE. Kevin, 2026-10-22, the rest of that rule: "you will
  // always have 1-5/8"" -- the Hood Sight Tube (confirmed; it is the only NDB question with a
  // 1-5/8" answer). LOCKED, not hidden: a hidden question encodes blank into the mapping, which
  // would send "no sight tube" to custitem_hoodsighttube. The same operation sets it.
  questionLockRules: [
    {
      questions: ["HOOD_SIGHT_TUBE"],
      lockedWhen: ACTUATOR_DESIGNS.map((d) => `(__["DESIGN/DESIGN"]=="${d}")`).join("||"),
    },
  ],

  // INLET CONNECTION SIZE: only the couplings that exist. Kevin, 2026-09-21: "these are the
  // only sizes that exist for inlet connection size. can you stop showing the ones that do not
  // exist but may exist in the future?" -- and then the eight aluminium full couplings, 1/4"
  // through 2".
  //
  // The export offers twenty-one fractions on every column, including 9/32", 11/32", 7/16",
  // 9/16", 1-3/4", 2-1/4", 2-1/2" and 2-3/4", none of which is a part. This narrows the
  // picker; it deletes nothing. The sizes that go are not wrong for ever -- when one is
  // stocked, add it back to this list and it returns, with its MAPPING token untouched
  // throughout, so a configuration saved against one of them still decodes.
  //
  // 11/16" CAME BACK ON THE AUX COLUMNS (Kevin, 2026-10-18). It is not a coupling, but it is
  // what the legacy Configuration tab records for an aux inlet on 337 sales orders since 2022
  // (39 in 2026, mostly Aux 1), and the grey pre-fill could never offer it. Main and Pilot
  // never used it, so they keep the eight couplings only.
  //
  // "None" is kept automatically where the column has it (the three Aux columns): it is the
  // absence of a connection, not a size.
  tableCellOptions: [
    {
      question: "SIZE_TABLE",
      columns: ["MAIN", "PILOT"],
      keep: ["1/4", "3/8", "1/2", "3/4", "1", "1 1/4", "1 1/2", "2"],
    },
    {
      question: "SIZE_TABLE",
      columns: ["AUX_1", "AUX_2", "AUX_3"],
      keep: ["1/4", "3/8", "1/2", "11/16", "3/4", "1", "1 1/4", "1 1/2", "2"],
    },
  ],

  optionalQuestionCodes: new Set([
    "INLET_CONNECTION_NOTES",
    "REQUIRED_HEAT_DUTY",
    "FUEL_PRESSURE",
  ]),


  // A SECOND manway offset a config may simply not have. The FIRST
  // (DIRECTION1/DISTANCE1) is not a pair, so it stays required whenever the manway is
  // offset.
  optionalQuestionPairs: [["DIRECTION2", "DISTANCE2"]],

  // FIRETUBE_DIAMETER ("Firetube Size") qualifies because leaving it blank changes
  // the generated PART NUMBER rather than just leaving a field empty: it is the
  // <ftDia+design> segment of the ID key, so a blank one silently drops that
  // segment (OT-SB16/12-6-1 becomes OT-SB16/12-1) and the item is named for a
  // configuration nobody specified. Blank is otherwise legal on the question record
  // (no hide rule, is_ready false), so the gate has to live here.
  // The three Face Plate questions joined FIRETUBE_DIAMETER on 2026-08-20: a half-answered
  // face plate may never be submitted. Each only becomes required once SHOWN -- Peep Sight
  // once a real Size is picked, Features once that peep sight is the 1" one -- and the whole
  // group hides on a Box-Type burner, which has no face plate. A hidden field cannot block,
  // so this demands exactly "whatever is on screen is filled in".
  hardRequiredQuestionCodes: new Set([
    "FIRETUBE_DIAMETER",
    "FACE_PLATE_SIZE",
    "FACE_PLATE_PEEPSIGHT",
    "FACE_PLATE_FEATURES",
  ]),

  // Required Heat Duty accepts "3,500,000" and "3.5MM". Without normalising,
  // Number("3,500,000") is NaN, every <= threshold comparison fails, and the Main
  // Assembly Size list comes back EMPTY.
  suffixNormalizedFields: new Set(["REQUIRED_HEAT_DUTY"]),

  // The three sections an FAH config (BURNER_QTY = "0 - FAH") collapses rather than
  // hides outright.
  fahSectionGroupCodes: new Set(["MAIN_BURNER", "PILOT", "IGNITOR"]),

  // Flange to Nozzle End carries all 81 values of customlistflange_to_nozzle_end, which is
  // far too many to scroll comfortably, so it is typeable as well as pickable.
  // HOODSIZE joined on 2026-08-20 (Kevin: "can you make hood size a combo box"). Its list is
  // 28 offered sizes over a NetSuite list of 54, so typing beats scrolling for the same
  // reason.
  comboBoxQuestionCodes: new Set(["FLANGE_TO_NOZZLE_END", "HOODSIZE"]),

  // And a size in NO list is created rather than refused. api/custom-answer adds it to the
  // question's NetSuite list and records it, and the endpoint's own validator is what stops a
  // typo becoming an absurd list value.
  //
  // FLANGE_TO_NOZZLE_END IS NOT HERE ANY MORE (2026-09-01). Its list cannot contribute another
  // usable value: the MAPPING token IS the NetSuite internal id in a 2-character slot, and
  // customlistflange_to_nozzle_end has passed id 99 -- `29-1/2"` was created and came back as
  // id 184, which would encode as "84", the token for 81". api/custom-answer refuses at that
  // ceiling, so every attempt to add a size (in practice every FRACTION, since 2"-81" whole
  // inches are all already listed) failed with "reached internal id 184". An unlisted size now
  // goes to unlistedSizeTextFallback below instead, which needs no list value at all.
  // FLANGE_TO_NOZZLE_END IS BACK (2026-10-19): its mapping slot is three characters wide now,
  // so a list id past 99 encodes, and Kevin wants the size in that field, not in the
  // extension-length text: "the flange to nozzle field should have been 39-3/4". The text
  // fallback below stays only to DISPLAY what the nine items built in between still carry.
  createsAnswersFor: new Set(["HOODSIZE", "FLANGE_TO_NOZZLE_END"]),

  // Hood Size creates without asking. Kevin, 2026-08-20: "it should be able to update its
  // netsuite list automatically without the user having to click add". Safe here precisely
  // BECAUSE of wholeNumberQuestionCodes below -- the only thing that can reach the list is an
  // integer number of inches, so there is no near-miss for a confirmation step to catch.
  //
  // FLANGE_TO_NOZZLE_END deliberately still asks: it takes sixteenths, where `19-3/4` and
  // `19-3/16` are one keystroke apart and both look entirely plausible.
  autoCreatesAnswersFor: new Set(["HOODSIZE"]),

  // Hood sizes are whole inches (Kevin, 2026-08-20), so 18-1/2 is a typo rather than a value.
  wholeNumberQuestionCodes: new Set(["HOODSIZE"]),

  // A custom nipple length that is not one of the listed sizes -- `31-1/2"` (Kevin,
  // 2026-09-01) -- is held as free text in EXT_NIP instead of becoming a list value, because
  // the list can no longer produce an encodable id (see createsAnswersFor above).
  //
  // EXT_NIP is the right home for it and needs nothing built: its MAPPING slot already exists
  // (EXT_NIPPLE_LENGTH, 8 characters of mixed inches, max_den 16) and already decodes to the
  // item's custitemmain_extension_length, a FREE-TEXT field that the shop already reads as
  // "MAIN EXTENSION LENGTH" on the WO traveler. So the mapping stays 367 characters and
  // netsuite/cpq2-mapping-decoder.js does NOT need re-pasting.
  //
  // EXT_NIP is a type-7 question, which mapping mode never nulls (see userFieldHidden in
  // util/runtime.ts) -- its value encodes even though the field itself is always hidden.
  //
  // Fingerprints do not move. EXT_NIP reaches the hash through BACKCOMPAT_TEXT in the ID Key
  // op, which appends a segment only when the value is NON-BLANK. A configuration on a listed
  // whole inch leaves it blank and hashes byte-identically to today, so no existing part
  // number changes and no duplicate item is built; only a fractional length -- which is a
  // genuinely different configuration -- gets a different fingerprint.
  unlistedSizeTextFallback: new Map([["FLANGE_TO_NOZZLE_END", ["EXT_NIP", "EXT_NIP"] as const]]),
  maxInches: new Map(),

  // CELL_OD (flame cell OD) is sized for every non-FAH burner and defines the item
  // whether or not its Flame Cells group is shown (that group hides for non-Box
  // housings), so nulling it would wrongly clear a valid attribute.
  mappingHideExemptCodes: new Set(["CELL_OD"]),

  // Matches this product's ITCR itemid template, {HIDDEN_ID_KEY/ID_KEY}.
  fingerprintAnswerKey: "HIDDEN_ID_KEY/ID_KEY",

  submit: {
    serialTag: true,
    additionalLines: true,
    listPricing: true,
    customerRequirements: true,
    ndbItemFields: true,
  },

  // Natural Draft writes no order body fields; its values reach the order through
  // the configured line and the item record.
  orderFields: [],

  // The live model beside the questions, and the customer drawing + model on the item.
  burnerModel: ndbBurnerModel,
};
